import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isAfter, startOfDay } from 'date-fns';
import { useOverlayContext } from 'seagull';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { AdHocSendOption, mapFormToAdHocSubmitRequest, resolveSubmitSemantics } from '../AdHocBillModal.types';
import { useAdHocSubmitMutation } from './useAdHocSubmitMutation';
const isIssueDateScheduledForFuture = (issueDate, now)=>isAfter(startOfDay(issueDate), startOfDay(now));
export const useAdHocSubmit = (param)=>{
    let { form, relationshipId, customerId, attachments, restoredAttachmentIds, legalTerms, defaultEmailSubject, defaultEmailContent, dismissModal } = param;
    const { submitAdHoc: submitAdHocMutation, isLoading, isError } = useAdHocSubmitMutation();
    const { showErrorToast, showSuccessToast } = useOverlayContext();
    const { getNow } = useIoContext();
    const queryClient = useQueryClient();
    const history = useHistory();
    const { t: adHocTexts } = useLocaleKeys('vendorOnboarding.adHocBillModal');
    const { t: commonTexts } = useLocaleKeys('common');
    const invalidateScheduledDraftCache = ()=>{
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.AdHocScheduledData,
                relationshipId
            ]
        });
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.InvoiceDraftByDate,
                relationshipId
            ]
        });
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.InvoiceDraftDates,
                relationshipId
            ]
        });
    };
    const showSuccessToastForOption = (option, scheduledForFuture)=>{
        switch(option){
            case AdHocSendOption.SendByEmail:
                if (scheduledForFuture) {
                    showSuccessToast(commonTexts.toasts.chargesSuccessfullyScheduled(), 'ad-hoc-bill-schedule-success-toast');
                } else {
                    showSuccessToast(commonTexts.toasts.chargesSuccessfullyCreated(), 'ad-hoc-bill-send-success-toast');
                }
                return;
            case AdHocSendOption.MarkPaidExternally:
                showSuccessToast(commonTexts.toasts.invoiceIssuedAndMarkedAsExternallyPaid(), 'ad-hoc-bill-paid-externally-success-toast');
                return;
            case AdHocSendOption.ShareByLink:
                return;
        }
    };
    const submitAdHoc = async (option, onSuccess)=>{
        var _legalTerms_find;
        const data = form.getValues();
        const scheduledForFuture = isIssueDateScheduledForFuture(data.issueDate, getNow());
        const semantics = resolveSubmitSemantics({
            option,
            isFutureIssueDate: scheduledForFuture
        });
        const selectedLegalTerm = (_legalTerms_find = legalTerms.find((lt)=>lt.id === data.termsOfServiceId)) !== null && _legalTerms_find !== void 0 ? _legalTerms_find : null;
        const restoredIds = new Set(restoredAttachmentIds);
        const newAttachments = attachments.filter((att)=>!restoredIds.has(att.id)).map((att)=>({
                id: att.id,
                displayName: att.displayName
            }));
        const request = mapFormToAdHocSubmitRequest({
            form: data,
            semantics,
            attachments: newAttachments,
            selectedLegalTerm,
            defaultEmailSubject,
            defaultEmailContent
        });
        const { success } = await handleRequest(submitAdHocMutation({
            relationshipId,
            data: request
        }));
        if (!success) {
            showErrorToast(adHocTexts.submitError(), 'ad-hoc-submit-error-toast');
            return false;
        }
        invalidateScheduledDraftCache();
        await queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.RelationshipSummaries
            ]
        });
        onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
        if (option === AdHocSendOption.ShareByLink) return true;
        showSuccessToastForOption(option, scheduledForFuture);
        history.push(routes.billingMinisiteRoutes({
            clientId: customerId,
            relationshipId
        }).invoices);
        dismissModal();
        return true;
    };
    return {
        submitAdHoc,
        isLoading,
        isError
    };
};
